import { ApolloClient, HttpLink, InMemoryCache, NormalizedCacheObject, Operation, split } from '@apollo/client';
import rcclUUID from '@rcg/uuid';
import ApolloLinkTimeout from 'apollo-link-timeout';
import merge from 'deepmerge';
import { getGraphParameter } from 'lib/blueGreen';
import { LocaleData } from 'lib/globalization/Locale/loadLocale';
import { useMemo } from 'react';
import { EnvironmentColor, ServerSideConfig } from 'types';
import { appendAkamaiHeaders } from '../utilities/akamaiHeaders';
import { logError, logInfo } from '../utilities/logger';
import { GraphClientName, GraphServer } from './constants';

const DEFAULT_CRUISE_SEARCH_TIMEOUT = 20000;

export let apolloClient: ApolloClient<NormalizedCacheObject>;

export function getSessionId(): string {
	if (typeof window !== 'undefined') {
		rcclUUID.createRWDId();

		return rcclUUID.getRWDId();
	}

	return undefined;
}

export function getURI(host: string | undefined, endpoint: string, envGraphColor?: EnvironmentColor): string {
	const queryParams: string = getGraphParameter(envGraphColor);
	const uri = `${endpoint}${queryParams}`;

	if (host && !host.includes('localhost')) {
		let baseUrl = `https://${host}`;

		if (host.includes('origin-tst-cruise-search.us-east-1.royalcaribbean.com') || host.includes('dev.royalcaribbean.com')) {
			baseUrl = 'https://www.test1.royalcaribbean.com';
		} else if (host.includes('dev.celebritycruises.com') || host.includes('dev.celebrity.com')) {
			baseUrl = 'https://www.stage1.celebritycruises.com';
		}

		return new URL(uri, baseUrl).toString();
	}

	return uri;
}

function isGraphqlServer(server: GraphServer): (op: Operation) => boolean {
	return (op: Operation) => op.getContext()?.server == server;
}

function createHttpLink(uri: string, headers: Record<string, string>, logId?: string) {
	const customFetch = async (input: string | URL | Request, init?: RequestInit) => {
		const logParams = {
			input,
			method: init.method,
			headers: init.headers,
			body: init.body ? JSON.parse(init.body.toString()) : undefined,
			logId,
		};

		try {
			const response = await fetch(input, init);

			// Clone so Apollo can still consume the original stream
			const responseJson = await response.clone().json();

			// Convert response headers to a plain object
			const headersObject: Record<string, string> = {};
			response.headers.forEach((value, key) => {
				headersObject[key] = value;
			});

			logInfo('Graph response', {
				...logParams,
				response: {
					status: response.status,
					headers: headersObject,
					body: responseJson,
				},
			});
			return response;
		} catch (error) {
			logError('Graph error', {
				...logParams,
				error,
			});
			throw error;
		}
	};
	return new HttpLink({
		uri,
		headers,
		fetch: customFetch,
	});
}

export interface ApolloClientProps {
	config: ServerSideConfig;
	host?: string;
	locale: LocaleData;
	envGraphColor?: EnvironmentColor;
	bypassApolloGateway?: boolean;
	logId?: string;
}

export function createApolloClient({ config, envGraphColor, bypassApolloGateway, host, locale, logId }: ApolloClientProps) {
	const { brand, endpoints } = config;

	const requestTimeout = endpoints.cruiseSearchTimeout ?? DEFAULT_CRUISE_SEARCH_TIMEOUT;

	const headers = appendAkamaiHeaders({
		brand: brand.code,
		country: locale?.country,
		language: locale?.language,
		currency: locale?.currency,
		office: locale?.office,
		countryalpha2code: locale?.countryAlpha2Code,
		'apollographql-client-name': GraphClientName.NEXT_GEN_CRUISE_SEARCH[brand.code],
		skip_authentication: 'true',
		'request-timeout': (requestTimeout / 1000).toString(), // convert ms timeout to s,
	});

	if (envGraphColor) {
		headers['x-env-color'] = envGraphColor;
	}

	const sessionId = getSessionId();
	if (sessionId !== undefined) {
		headers['x-session-id'] = sessionId;
	}

	const cruiseSearchUri = getURI(
		host,
		bypassApolloGateway ? config.endpoints.cruiseStandaloneSearchApi : config.endpoints.cruiseSearchApi,
		envGraphColor,
	);
	const globalizationUri = getURI(host, endpoints.globalizationApi, envGraphColor);
	const campaignsUri = getURI(host, endpoints.campaignsApi, envGraphColor);

	const timeoutLink = new ApolloLinkTimeout(requestTimeout);
	const cruiseSearchLink = createHttpLink(cruiseSearchUri, headers, logId);
	const globalizationLink = createHttpLink(globalizationUri, headers, logId);
	const campaignsLink = createHttpLink(campaignsUri, headers, logId);
	const splitLink = split(
		isGraphqlServer(GraphServer.GLOBALIZATION),
		globalizationLink,
		split(isGraphqlServer(GraphServer.CAMPAIGNS), campaignsLink, cruiseSearchLink),
	);

	const timeoutHttpLink = timeoutLink.concat(splitLink);

	const client = new ApolloClient({
		cache: new InMemoryCache(),
		link: timeoutHttpLink,
	});

	return client;
}

export function initializeApollo({ config, envGraphColor, bypassApolloGateway, host, locale, logId }: ApolloClientProps) {
	const _apolloClient = apolloClient ?? createApolloClient({ config, envGraphColor, bypassApolloGateway, host, locale, logId });

	// If your page has Next.js data fetching methods that use Apollo Client, the initial state
	// gets hydrated here
	if (locale) {
		// Get existing cache, loaded during client side data fetching
		const existingCache = _apolloClient.extract();
		// Merge the existing cache into data passed from getStaticProps/getServerSideProps
		const data = merge(locale, existingCache, {
			// combine arrays using object equality (like in sets)
			arrayMerge: (destinationArray, sourceArray) => [
				...sourceArray,
				...destinationArray.filter((d) => sourceArray.every((s) => JSON.stringify(d) !== JSON.stringify(s))),
			],
		});
		// Restore the cache with the merged data
		_apolloClient.cache.restore(data);
	}
	// For SSG and SSR always create a new Apollo Client
	if (typeof window === 'undefined') {
		return _apolloClient;
	}
	// Create the Apollo Client once in the client
	if (!apolloClient) {
		apolloClient = _apolloClient;
	}

	return _apolloClient;
}

export function useApollo({ config, envGraphColor, bypassApolloGateway, host, locale, logId }: ApolloClientProps) {
	const store = useMemo(
		() => initializeApollo({ config, envGraphColor, bypassApolloGateway, host, locale, logId }),
		[config, envGraphColor, host, locale, logId],
	);
	return store;
}
